import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Scale, Camera, Video, CheckCircle2 } from 'lucide-react'
import { getSmartKraalWeighings } from '../services/api'
import { BIND_METHOD_LABEL, isLiveBinding } from './SmartKraalBindingChip'
import { formatEAT } from '../utils/presence'

// Kampala time lives in utils/presence.js; the SmartKraal pages import these two
// from here, so they stay exported.
export { formatEAT, kampalaToday } from '../utils/presence'


/**
 * "SmartKraal weighings" section for the animal page (D11 phase 1).
 *
 * Every reading the scale produced for this animal, newest first, with its
 * confidence. Readings below the display floor stay visible but greyed —
 * never hidden. A tick marks the readings that became the farm's weight
 * records (cattle_events 'weighing' via the promotion pass).
 * `embedded` renders the table without its own card and links: the kraal
 * timeline (plan B1) keeps it under "All scale readings".
 */
export default function SmartKraalWeighings({ animalId, embedded = false }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['smartkraalWeighings', animalId],
    queryFn: ({ signal }) => getSmartKraalWeighings(animalId, { signal }),
    enabled: !!animalId,
    staleTime: 5 * 60 * 1000,
  })

  if (isLoading || error || !data || data.configured === false) return null
  const readings = data.readings || []
  if (readings.length === 0) return null
  const floor = data.min_confidence_display

  return (
    <div className={embedded ? '' : 'card p-6'} data-testid="smartkraal-weighings">
      <div className="flex items-center justify-between mb-4">
        <h3 className={`font-medium text-primary-900 flex items-center gap-2 ${embedded ? 'text-sm' : 'text-lg'}`}>
          <Scale className={`text-amber-600 ${embedded ? 'h-4 w-4' : 'h-5 w-5'}`} />
          SmartKraal weighings
          <span className="text-sm font-normal text-earth-500">({readings.length})</span>
        </h3>
        {!embedded && (
          <div className="flex items-center gap-3 text-sm">
            <Link to="/cattle/registration" className="text-primary-600 hover:text-primary-800">Registration</Link>
            <Link to="/cattle/smartkraal" className="text-primary-600 hover:text-primary-800">Sync health</Link>
          </div>
        )}
      </div>
      {isLiveBinding(data.tag_link) && (
        <p className="text-xs text-earth-600 -mt-2 mb-3" data-testid="bound-via">
          Readings reach this animal through SmartKraal tag <span className="font-semibold">{data.tag_link.p_idtag}</span>
          {' '}(bound via {BIND_METHOD_LABEL[data.tag_link.bind_method] || data.tag_link.bind_method}
          {data.tag_link.bound_at ? ` on ${formatEAT(data.tag_link.bound_at)}` : ''}).{' '}
          {data.tag_link.status === 'sent_back'
            ? 'The reviewer sent this binding back: weights are withheld until it is re-bound.'
            : data.tag_link.status === 'confirmed' ? 'Binding confirmed by the reviewer.' : 'Binding awaiting review.'}
          {data.tag_link.review_note ? ` Note: “${data.tag_link.review_note}”` : ''}
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="text-left text-earth-500 border-b border-earth-200">
              <th className="py-2 pr-4">When</th>
              <th className="py-2 pr-4 text-right">Weight</th>
              <th className="py-2 pr-4 text-right">Confidence</th>
              <th className="py-2 pr-4">In records</th>
              <th className="py-2 pr-4">Media</th>
            </tr>
          </thead>
          <tbody>
            {readings.map((r) => {
              const dim = r.below_display_floor || r.ambiguous || r.excluded
              return (
                <tr key={`${r.a_idsmartkraal}-${r.p_idevent}`} className={dim ? 'text-earth-400' : 'text-earth-800'}>
                  <td className="py-2 pr-4 whitespace-nowrap">{formatEAT(r.ts_utc)}</td>
                  <td className="py-2 pr-4 text-right font-medium">
                    {r.weight_kg != null ? `${r.weight_kg} kg` : '—'}
                  </td>
                  <td className="py-2 pr-4 text-right">
                    <span className={`inline-block rounded px-2 py-0.5 text-xs ${
                      r.confidence == null ? 'bg-earth-100' :
                      r.confidence >= 90 ? 'bg-primary-100 text-primary-800' :
                      r.confidence >= floor ? 'bg-amber-100 text-amber-800' : 'bg-earth-100 text-earth-500'
                    }`}>
                      {r.confidence ?? '—'}
                    </span>
                    {r.ambiguous && <span className="ml-1 text-xs">ambiguous</span>}
                  </td>
                  <td className="py-2 pr-4">
                    {r.in_farm_records ? (
                      <span className="inline-flex items-center gap-1 text-primary-700">
                        <CheckCircle2 className="h-4 w-4" /> yes
                      </span>
                    ) : r.is_best_daily ? (
                      <span className="text-xs text-earth-400">best of day, not promoted</span>
                    ) : (
                      <span className="text-xs text-earth-400">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-2">
                      {r.media?.cam1_still && (
                        <a href={r.media.cam1_still} target="_blank" rel="noreferrer" title="Camera 1 still"
                           className="text-primary-600 hover:text-primary-800"><Camera className="h-4 w-4" /></a>
                      )}
                      {r.media?.cam2_still && (
                        <a href={r.media.cam2_still} target="_blank" rel="noreferrer" title="Camera 2 still"
                           className="text-primary-600 hover:text-primary-800"><Camera className="h-4 w-4" /></a>
                      )}
                      {r.media?.cam1_clip && (
                        <a href={r.media.cam1_clip} target="_blank" rel="noreferrer" title="Clip"
                           className="text-primary-600 hover:text-primary-800"><Video className="h-4 w-4" /></a>
                      )}
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-earth-500">
        Confidence is the scale's own score for each reading; readings under {floor} are shown greyed.
        Times are East Africa Time.
      </p>
    </div>
  )
}
